/**
 * ts-spin.js — True Silver 360 product spinner.
 *
 * Any product whose media includes images with alt text starting "spin-",
 * OR whose image filename contains "spin-N" (spin-1b.png ...), gets a
 * drag-to-rotate viewer in place of the main gallery image. Frames are
 * ordered by that number (spin-1, spin-2, ...). The filename fallback exists
 * because product image alts get rewritten for SEO (the Eyes Up Trainer's
 * alts were renamed 2026-09-07 and the spinner vanished).
 *
 * Drag left/right, use the arrow keys, or let it auto-spin once on first view.
 * Falls back silently to the normal gallery when fewer than two frames exist.
 */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function frameIndex(im) {
    const byAlt = (im.alt || '').match(/^spin-(\d+)/);
    if (byAlt) return parseInt(byAlt[1], 10);
    const src = (im.currentSrc || im.src || '').split('?')[0];
    const byName = src.match(/\/spin-(\d+)[a-z]?\.(?:png|jpe?g|webp)$/i);
    return byName ? parseInt(byName[1], 10) : null;
  }

  function collectFrames() {
    const imgs = [...document.querySelectorAll('.product__media-wrapper img, .product__media-list img, .product__media img')];
    const seen = new Map();
    imgs.forEach((im) => {
      const n = frameIndex(im);
      if (n === null) return;
      // strip Shopify's size suffix so we can request one consistent width
      const base = (im.currentSrc || im.src).split('?')[0];
      if (!seen.has(n)) seen.set(n, base + '?width=1200');
    });
    return [...seen.entries()].sort((a, b) => a[0] - b[0]).map((e) => e[1]);
  }

  function build(frames) {
    const host = document.querySelector('.product__media-wrapper') ||
                 document.querySelector('.product__media-list')?.parentElement;
    if (!host) return;

    const wrap = document.createElement('div');
    wrap.className = 'ts-spin';
    wrap.innerHTML =
      '<div class="ts-spin__stage">' +
        frames.map((f, i) =>
          '<img class="ts-spin__frame" src="' + f + '" alt="" draggable="false"' +
          (i === 0 ? ' data-on="1"' : '') + '>').join('') +
      '</div>' +
      '<div class="ts-spin__ui">' +
        '<span class="ts-spin__hint">Drag to rotate</span>' +
        '<div class="ts-spin__dots">' +
          frames.map((_, i) => '<button class="ts-spin__dot" data-i="' + i +
            '" aria-label="View ' + (i + 1) + '"></button>').join('') +
        '</div>' +
      '</div>';

    host.prepend(wrap);

    const stage = wrap.querySelector('.ts-spin__stage');
    const els = [...wrap.querySelectorAll('.ts-spin__frame')];
    const dots = [...wrap.querySelectorAll('.ts-spin__dot')];
    let idx = 0;

    const show = (i) => {
      idx = ((i % els.length) + els.length) % els.length;
      els.forEach((e, n) => e.toggleAttribute('data-on', n === idx));
      dots.forEach((d, n) => d.toggleAttribute('data-on', n === idx));
    };

    // ---- drag ----
    let dragging = false, lastX = 0, acc = 0;
    const STEP = 34; // px of drag per frame

    const down = (x) => { dragging = true; lastX = x; acc = 0; wrap.setAttribute('data-drag', ''); };
    const move = (x) => {
      if (!dragging) return;
      acc += x - lastX;
      lastX = x;
      while (Math.abs(acc) >= STEP) {
        show(idx + (acc > 0 ? 1 : -1));
        acc += acc > 0 ? -STEP : STEP;
        wrap.removeAttribute('data-idle');
      }
    };
    const up = () => { dragging = false; wrap.removeAttribute('data-drag'); };

    stage.addEventListener('pointerdown', (e) => { down(e.clientX); stage.setPointerCapture(e.pointerId); });
    stage.addEventListener('pointermove', (e) => move(e.clientX));
    stage.addEventListener('pointerup', up);
    stage.addEventListener('pointercancel', up);
    stage.addEventListener('dragstart', (e) => e.preventDefault());

    dots.forEach((d) => d.addEventListener('click', () => {
      show(parseInt(d.dataset.i, 10));
      wrap.removeAttribute('data-idle');
    }));

    wrap.tabIndex = 0;
    wrap.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { show(idx + 1); e.preventDefault(); }
      if (e.key === 'ArrowLeft') { show(idx - 1); e.preventDefault(); }
    });

    show(0);

    // ---- one gentle auto-spin so people know it moves ----
    if (!reduce) {
      wrap.setAttribute('data-idle', '');
      let n = 0;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          io.disconnect();
          const t = setInterval(() => {
            if (!wrap.hasAttribute('data-idle') || n >= els.length) { clearInterval(t); return; }
            show(idx + 1); n++;
          }, 420);
        });
      }, { threshold: 0.4 });
      io.observe(wrap);
    }
  }

  function init() {
    if (document.querySelector('.ts-spin')) return;
    const frames = collectFrames();
    if (frames.length < 2) return;
    build(frames);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setTimeout(init, 400));
  } else {
    setTimeout(init, 400);
  }
})();
